import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { inputCls, labelCls, btnPrimary } from "@/lib/constants";
import { Modal } from "@/components/Modal";
import { toast } from "sonner";
import { Plus, Trash2, Settings2, ToggleLeft, ToggleRight, Package } from "lucide-react";

const FIELD_TYPES = ["text", "number", "date", "select"];
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");

export default function Products() {
  const [products, setProducts] = useState([]);
  const [newName, setNewName] = useState("");
  const [fieldsFor, setFieldsFor] = useState(null);
  const [fields, setFields] = useState([]);

  const load = useCallback(() => api.get("/products").then((r) => setProducts(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    try { await api.post("/products", { name: newName.trim() }); toast.success("Product added"); setNewName(""); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const toggle = async (p) => { await api.put(`/products/${p.id}`, { active: !p.active }); load(); };
  const del = async (p) => { if (!window.confirm(`Delete "${p.name}"?`)) return; await api.delete(`/products/${p.id}`); toast.success("Product deleted"); load(); };

  const openFields = (p) => { setFieldsFor(p); setFields((p.fields || []).map((f) => ({ ...f }))); };
  const addField = () => setFields((f) => [...f, { key: "", label: "", type: "text", required: false, options: [] }]);
  const updField = (i, k, v) => setFields((f) => f.map((x, idx) => idx === i ? { ...x, [k]: v, ...(k === "label" ? { key: slug(v) } : {}) } : x));
  const removeField = (i) => setFields((f) => f.filter((_, idx) => idx !== i));
  const saveFields = async () => {
    const clean = fields.filter((f) => f.label).map((f) => ({
      key: f.key || slug(f.label), label: f.label, type: f.type, required: !!f.required,
      options: f.type === "select" ? (Array.isArray(f.options) ? f.options : String(f.options || "").split(",").map((s) => s.trim()).filter(Boolean)) : [],
    }));
    await api.put(`/products/${fieldsFor.id}`, { fields: clean });
    toast.success("Custom fields saved"); setFieldsFor(null); load();
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 font-heading tracking-tight">Products</h1>
        <p className="text-sm text-slate-500 mt-1">Manage loan products and their product-specific lead fields</p>
      </div>

      <form onSubmit={add} className="flex gap-3">
        <input data-testid="product-name-input" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="New product name (e.g. Gold Loan)" className={`${inputCls} max-w-sm`} />
        <button data-testid="add-product-button" type="submit" className={btnPrimary}><Plus size={16} /> Add Product</button>
      </form>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {products.map((p) => (
          <div key={p.id} data-testid={`product-card-${p.id}`} className="p-5 rounded-xl bg-white border border-slate-200 shadow-sm">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 text-emerald-600 flex items-center justify-center"><Package size={20} /></div>
                <div>
                  <div className="font-bold text-slate-900 font-heading">{p.name}</div>
                  <div className="text-xs text-slate-400">{(p.fields || []).length} custom field(s)</div>
                </div>
              </div>
              <button data-testid={`delete-product-${p.id}`} onClick={() => del(p)} className="p-1.5 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50"><Trash2 size={15} /></button>
            </div>
            <div className="flex items-center gap-2 mt-4">
              <button data-testid={`toggle-product-${p.id}`} onClick={() => toggle(p)} className={`flex items-center gap-1 text-xs font-medium ${p.active ? "text-emerald-600" : "text-slate-400"}`}>
                {p.active ? <ToggleRight size={18} /> : <ToggleLeft size={18} />} {p.active ? "Active" : "Inactive"}
              </button>
              <button data-testid={`fields-product-${p.id}`} onClick={() => openFields(p)} className="ml-auto text-xs px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 flex items-center gap-1"><Settings2 size={13} /> Custom Fields</button>
            </div>
          </div>
        ))}
      </div>

      <Modal open={!!fieldsFor} onClose={() => setFieldsFor(null)} title={`Custom Fields · ${fieldsFor?.name || ""}`} size="lg" testId="fields-modal">
        <div className="space-y-3">
          {fields.length === 0 && <p className="text-sm text-slate-400">No custom fields. Add fields shown when creating a lead of this product.</p>}
          {fields.map((f, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center">
              <input data-testid={`field-label-${i}`} value={f.label} onChange={(e) => updField(i, "label", e.target.value)} placeholder="Field label" className={`${inputCls} col-span-4`} />
              <select value={f.type} onChange={(e) => updField(i, "type", e.target.value)} className={`${inputCls} col-span-3`}>
                {FIELD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <input value={Array.isArray(f.options) ? f.options.join(", ") : (f.options || "")} onChange={(e) => updField(i, "options", e.target.value)} placeholder={f.type === "select" ? "opt1, opt2" : "—"} disabled={f.type !== "select"} className={`${inputCls} col-span-3 disabled:bg-slate-50`} />
              <label className="col-span-1 flex items-center gap-1 text-xs text-slate-600"><input type="checkbox" checked={!!f.required} onChange={(e) => updField(i, "required", e.target.checked)} className="accent-emerald-600" />Req</label>
              <button onClick={() => removeField(i)} className="col-span-1 p-2 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50"><Trash2 size={15} /></button>
            </div>
          ))}
          <button data-testid="add-field-button" onClick={addField} className="text-sm text-emerald-600 font-medium flex items-center gap-1"><Plus size={15} /> Add field</button>
          <div className="flex justify-end gap-3 pt-3 border-t border-slate-100">
            <button onClick={() => setFieldsFor(null)} className="px-4 py-2 rounded-lg text-slate-600 hover:bg-slate-100 text-sm font-medium">Cancel</button>
            <button data-testid="save-fields-button" onClick={saveFields} className={btnPrimary}>Save Fields</button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
